// @flow
import point10Messages from 'esm-web-pro/messages/points10Messages';
import * as React from 'react';
import { IntlShape, injectIntl, defineMessages } from 'react-intl';
import Attributes from 'esm-web-pro/components/layout/sidePanel/Attributes';
import type { Pipe } from 'esm-web-pro/containers/pages/network/objects/pipe/Pipe';
import attributeMessages from 'esm-web-pro/messages/attributeMessages';
import point11Messages from 'esm-web-pro/messages/points11Messages';
import StyledFormCheckmark from 'esm-web-pro/components/layout/sidePanel/icons/StyledFormCheckmark';
import { FormClose } from 'grommet-icons';

type Props = {
    pipe: Pipe,
    url: string,
    intl: IntlShape,
    onView: Function,
}

const PIPE_CONTAINER_TYPE = 'gisline';

const msg = defineMessages({
    route: {
        id: 'route-container-point',
        defaultMessage: 'Route',
    },
});

const PipeAttributes = (props: Props) => {
    const {
        pipe, url, intl: { formatMessage }, onView,
    } = props;
    const {
        infrastructure_provider: infrastructureProvider, type, start_termination_point: startTerminationPoint,
        end_termination_point: endTerminationPoint, start_container_point: startContainerPoint,
        end_container_point: endContainerPoint, gis_line: gisLine, export: eitExport, provider_ref: providerRef,
        is_shared: isShared, continuous_pipe: continuousPipe, property_owner: propertyOwner,
        project, project_stage: projectStage,
    } = pipe;

    const pointAttribute = (terminationPoint, containerPoint) => (terminationPoint
        ? {
            value: `${formatMessage(point11Messages[terminationPoint.type])} ${
                terminationPoint.type === 'pop' && terminationPoint.alias
                    ? terminationPoint.alias
                    : terminationPoint.sequence
            }`,
            link: `${url.split('/layer')[0]}/layer/${terminationPoint.type}/${terminationPoint.identity}`,
        }
        : {
            value: `${formatMessage(point10Messages.gispoint)} ${containerPoint.sequence}`,
            link: `${url.split('/layer')[0]}/layer/gispoint/${containerPoint.identity}`,
        });

    const attributes = [
        {
            label: formatMessage(attributeMessages.provider),
            value: infrastructureProvider?.entity?.name,
        },
        {
            label: formatMessage(attributeMessages.ref),
            value: pipe.ref,
            onClick: onView,
        },
    ];

    if (providerRef) {
        attributes.push({
            label: formatMessage(attributeMessages.providerRef),
            value: providerRef,
        });
    }

    if (type) {
        attributes.push({
            label: formatMessage(attributeMessages.model),
            value: type,
            link: type ? `${url}/model/${type.code}`.replace('//', '/') : '',
        });
    }

    if (continuousPipe) {
        attributes.push({
            label: formatMessage(attributeMessages.continuousPipe),
            value: continuousPipe.ref,
            link: `${url.split('/layer')[0]}/layer/continuousPipe/${continuousPipe.identity}`,
        });
    }

    if (propertyOwner) {
        attributes.push({
            label: formatMessage(attributeMessages.propertyOwner),
            value: propertyOwner.name,
            link: `/counterparties/${propertyOwner.uuid}`,
        });
    }

    if (project) {
        attributes.push({
            label: formatMessage(attributeMessages.project),
            value: project.name,
            link: `/projects/${project.uuid}/dashboard/stages`,
        });
    }
    if (project && projectStage) {
        attributes.push({
            label: formatMessage(attributeMessages.projectStage),
            value: projectStage.name,
            link: `/projects/${project.uuid}/dashboard/stages/quick-view/${projectStage.uuid}`,
        });
    }

    attributes.push(...[{
        label: formatMessage(attributeMessages.container),
        value: `${formatMessage(msg.route)} ${gisLine.ref}`,
        link: `${url.split('/layer')[0]}/layer/${PIPE_CONTAINER_TYPE}/${gisLine.identity}`,
    },
    {
        label: formatMessage(attributeMessages.start),
        ...pointAttribute(startTerminationPoint, startContainerPoint),
    },
    {
        label: formatMessage(attributeMessages.end),
        ...pointAttribute(endTerminationPoint, endContainerPoint),
    },
    {
        label: formatMessage(attributeMessages.export),
        value: eitExport ? <StyledFormCheckmark /> : <FormClose />,
    },
    {
        label: formatMessage(attributeMessages.shared),
        value: isShared ? <StyledFormCheckmark /> : <FormClose />,
    }]);

    return (
        <Attributes attributes={attributes} />
    );
};

export default injectIntl(PipeAttributes);
